import { PaginatedResource } from '../../typings/Paginated';
import { translate } from '../translate';
import { AlreadyExists, BaseService, ClientError, ExtendableError, GenericPaginatedParameters, InvalidValue, NotFound, PartialBy, Unauthorized, UnexpectedError } from './base-service';
import { HasOpenVacancies } from './deac';
import { GetResourcePaginatedByActingBodyParameters } from './dispatch';


interface CreateGroupParameters {
    name: string;
    description: string;
    permissions: GroupPermissions;
    /**
     * List of user IDs to be added
     */
    usersToAdd: number[];
    /**
     * List of cameras IDs to be added
     */
    camerasToAdd: string[];
}

interface UpdateGroupParameters extends CreateGroupParameters {
    /**
     * List of user IDs to be removed
     */
    usersToRm: number[];
    /**
     * List of cameras IDs to be removed
     */
    camerasToRm: string[];
}

interface UserParameters {
    name: string;
    email: string;
    registry: string;
    phone: string;
    document: string;
    /**
     * List of group IDs to be added
     */
    groupsToAdd: number[];
    /**
     * List of permission level IDs to be added
     */
    permissionLevelsToAdd: number[];
    /**
     * List of skills IDs to be added
     */
    skillsToAdd: number[];
    workplacesToBlock: number[];
    roleId: number;
    unitId: number;
    patent?: string;
    warName?: string;
    tags?: number[];
    vacationPeriodStart?: number;
    vacationPeriodEnd?: number;
    leaveOfAbsenceStart?: number;
    leaveOfAbsenceEnd?: number;
    agentScheduleId?: number;
    isDailyWorkSchedule?: boolean;
}

interface CreateUserParameters extends UserParameters {
    password: string;
    actingBodyId?: number;
}

interface UpdateUserParameters extends UserParameters {
    /**
     * List of group IDs to be removed
     */
    groupsToRm: number[];
    /**
     * List of permission level IDs to be removed
     */
    permissionLevelsToRm: number[];
    /**
     * List of skills IDs to be removed
     */
    skillsToRm: number[];
    workplacesToUnblock: number[];
    password?: string;
    actingBodyId?: number;
}

interface CreatePermissionLevelParameters {
    name: string;
    description: string;
    sessionValidityTime: number;
    sessionQuantityLimit: number;
    mobileSessionValidityTime: number;
    mobileSessionQuantityLimit: number;
    permissions: UserPermissions;
    /**
     * List of user IDs to be added
     */
    usersToAdd: number[];
}

interface PaginatedUsersParams extends GenericPaginatedParameters {
    phone?: string;
    document?: string;
    role?: number;
    actingBodyId?: number;
    actingBodyUnitId?: number;
}

interface UpdatePermissionLevelParameters extends CreatePermissionLevelParameters {
    /**
     * List of user IDs to be removed
     */
    usersToRm: number[];
}

interface UpdateTurnstileParameters {
    /**
     * List of user IDs to be added
     */
    usersToAdd: number[];
    /**
     * List of user IDs to be removed
     */
    usersToRm: number[];
}

interface GetPaginatedGroupsParameters extends GenericPaginatedParameters {
    actingBodyId?: number;
    actingBodyUnitIds?: number[];
    userId?: number;
    cameras?: string[];
}

export class InvalidIpAddress extends ExtendableError {
    ipAddress: string | undefined;
    constructor(code: string, ipAddress: string) {
        super(code);
        this.ipAddress = ipAddress;
    }
}

export class IpAddressAlreadyExists extends ExtendableError {
    ipAddress: string | undefined;
    constructor(code: string, ipAddress: string) {
        super(code);
        this.ipAddress = ipAddress;
    }
}

interface GetPaginatedPermissionLevelsParameters extends GenericPaginatedParameters {
    actingBodyId?: number;
    actingBodyUnitIds?: number[];
    userId?: number;
    permissions?: (keyof UserPermissions)[];
}

interface ActingBodySimplified {
    id: number;
    name: string;
}

export interface SimplifiedOccurrenceType extends Omit<BaseOccurrenceType, 'active' | 'actingBodyId'> { }

interface CreateSkillParameters {
    name: string;
    description: string;
    actingBodyId?: number;
    /**
     * List of user IDs to be added
     */
    usersToAdd: number[];
}

interface UpdateSkillParameters extends CreateSkillParameters {
    /**
     * List of user IDs to be removed
     */
    usersToRm: number[];
}

class AccessService extends BaseService {

    async getAccessAllUsers({ textFilter, actingBodyId, actingBodyUnitId }: {
        textFilter?: string;
        actingBodyId?: number;
        actingBodyUnitId?: number;
    }): Promise<AccessSimplifiedUserData[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/access/users${this.encodeQueryParams({ textFilter, actingBodyId, actingBodyUnitId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });
        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getActingBodies(): Promise<ActingBodySimplified[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/acting-bodies-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDropDownOccurrenceTypes(actingBodyId?: number): Promise<SimplifiedOccurrenceType[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/simplified-occurrence-types${this.encodeQueryParams({
            actingBodyId
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPaginatedActingBodies(params: GenericPaginatedParameters): Promise<PaginatedResource<ActingBody>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-bodies${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getAllCameras({ textFilter }: { textFilter?: string; }): Promise<AccessSimplifiedCameraData[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/access/cameras${this.encodeQueryParams({ textFilter })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        const cameraList = await res.json();

        return cameraList.map((camera: AccessSimplifiedCameraData) => {
            if (!camera.tagList) {
                return camera;
            }

            //We do not want to filter on the back, but it must be done somewhere
            camera.tagList = JSON.parse(String(camera.tagList));
            return camera;
        });

    }

    async getPaginatedAlertGroupCameras(params: Omit<GenericPaginatedParameters, 'actingBodyId'> & { mapPolygonId?: number; }): Promise<PaginatedResource<AccessSimplifiedCameraData>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/access/paginated-cameras${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        const cameraList = await res.json();
        return {
            count: cameraList.count,
            rows: cameraList.rows.map((camera: AccessSimplifiedCameraData) => {
                if (!camera.tagList) {
                    return camera;
                }

                //We do not want to filter on the back, but it must be done somewhere
                camera.tagList = JSON.parse(String(camera.tagList));
                return camera;
            })
        };
    }

    async getUsers(params: PaginatedUsersParams): Promise<PaginatedResource<UserData>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/users${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUser(id: number): Promise<UserData & { Groups: GroupData[]; PermissionLevels: (PermissionLevelData & { permissions: { id: number; permission: string; }[]; })[]; Skills: Skill[]; tags: Tag[]; BlockedWorkplaces: Workplace[]; }> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {ClientError} EmailAlreadyExists
     * @returns
     */
    async createUser(params: CreateUserParameters): Promise<UserData> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {ClientError} EmailAlreadyExists
     * @returns
     */
    async updateUser(id: number, params: UpdateUserParameters): Promise<UserData> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteUser(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getGroups(params: GetPaginatedGroupsParameters): Promise<PaginatedResource<GroupData>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/groups${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getGroup(id: number): Promise<AccessSimplifiedGroupData> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/group/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createGroup({ name, description, usersToAdd, camerasToAdd, permissions, unitId }: (CreateGroupParameters & { unitId: number; })): Promise<GroupData> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/group`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, description, usersToAdd, camerasToAdd, permissions, unitId }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateGroup(id: number, { name, description, usersToAdd, camerasToAdd, usersToRm, camerasToRm, permissions }: UpdateGroupParameters): Promise<GroupData> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/group/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, description, usersToAdd, camerasToAdd, usersToRm, camerasToRm, permissions }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteGroup(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/group/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    // ---------- ACTING BODY UNIT ----------
    async getPaginatedActingBodyUnits(params: GenericPaginatedParameters): Promise<PaginatedResource<ActingBodyUnit>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body-units${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async deleteActingBodyUnit(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body-unit/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

    }

    async createActingBodyUnit(actingBodyUnit: Omit<ActingBodyUnit, 'id' | 'createdAt'>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body-unit`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ ...actingBodyUnit }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async updateActingBodyUnit(actingBodyUnit: PartialBy<Omit<ActingBodyUnit, 'createdAt' | 'actingBodyId'>, 'id'> & { effectiveDate: Date; reissueAllReports: boolean; }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body-unit/${actingBodyUnit.id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                ...actingBodyUnit,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getActingBodyUnitById(id: number): Promise<ActingBodyUnit> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body-unit/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }
    // ---------- ACTING BODY UNIT ----------

    // ---------- COMMAND ----------
    async getActingBodyCommandById(id: number): Promise<ActingBodyCommand> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body-command/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPaginatedActingBodyCommands(params: GenericPaginatedParameters): Promise<PaginatedResource<ActingBodyCommand>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body-commands${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async createActingBodyCommand(command: Omit<ActingBodyCommand, 'id'>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body-command`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ ...command }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async updateActingBodyCommand(command: PartialBy<Omit<ActingBodyCommand, 'actingBodyId'>, 'id'>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body-command/${command.id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(command)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async deleteActingBodyCommand(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body-command/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

    }
    // ---------- COMMAND ----------

    // ---------- ROLES ----------
    async getRoleById(id: number): Promise<Role> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/role/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPaginatedRoles(params: GenericPaginatedParameters): Promise<PaginatedResource<Role>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/roles${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async createRole(role: Omit<Role, 'id'>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/role`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ ...role }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async updateRole(role: PartialBy<Omit<Role, 'actingBodyId'>, 'id'>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/role/${role.id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(role)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async deleteRole(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/role/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

    }
    // ---------- ROLES ----------

    async getPermissionLevels(params: GetPaginatedPermissionLevelsParameters): Promise<PaginatedResource<PermissionLevelData>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/permission-levels${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getHikTurnstiles({ page, limit }: GenericPaginatedParameters): Promise<PaginatedResource<HikTurnstile>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/hik-turnstiles${this.encodeQueryParams({ page, limit })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getHikTurnstile(id: number): Promise<HikTurnstile & { Users: UserData[]; }> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/hik-turnstile/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateHikTurnstile(id: number, { usersToAdd, usersToRm }: UpdateTurnstileParameters): Promise<HikTurnstile> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/hik-turnstile/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                usersToAdd,
                usersToRm,
            }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPermissionLevel(id: number): Promise<PermissionLevelData & { Users: UserData[]; permissionObj: UserPermissions; }> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/permission-level/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createPermissionLevel({ name, description, usersToAdd, permissions, actingBodyUnitId, sessionValidityTime, sessionQuantityLimit, mobileSessionValidityTime, mobileSessionQuantityLimit }: (CreatePermissionLevelParameters & { actingBodyUnitId: number; })): Promise<PermissionLevelData> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/permission-level`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, description, usersToAdd, permissions, unitId: actingBodyUnitId, sessionValidityTime, sessionQuantityLimit, mobileSessionValidityTime, mobileSessionQuantityLimit }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updatePermissionLevel(id: number, { name, description, usersToAdd, usersToRm, permissions, sessionValidityTime, sessionQuantityLimit, mobileSessionValidityTime, mobileSessionQuantityLimit }: UpdatePermissionLevelParameters): Promise<PermissionLevelData> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/permission-level/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, description, usersToAdd, usersToRm, permissions, sessionValidityTime, sessionQuantityLimit, mobileSessionValidityTime, mobileSessionQuantityLimit }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deletePermissionLevel(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/permission-level/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getActingBody(id: number, queryParams?: { includeFacialInfo?: boolean; }): Promise<ActingBody> {
        const token = await this.getToken();

        const isAgentApp = process.env.AGENT_FRONTEND_DOMAIN ? window.location.host.startsWith(process.env.AGENT_FRONTEND_DOMAIN) : false;

        const res = await fetch(`${this.centralEndpoint}${isAgentApp ? '/app-agent' : ''}/acting-body/${id}${this.encodeQueryParams(queryParams)}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteActingBody(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async createActingBody(actingBody: Omit<ActingBody, 'id' | 'externalOccurrenceTypeWantedPerson' | 'externalOccurrenceTypeMissingPerson' | 'defaultLprOccurrenceTypeId'>): Promise<ActingBody> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/acting-body`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(actingBody)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateActingBody(actingBody: ActingBody): Promise<ActingBody> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/acting-body/${actingBody.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(actingBody)
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getInstallationCompanies({ page, limit }: GenericPaginatedParameters): Promise<PaginatedResource<InstallationCompanyList>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/installation-companies${this.encodeQueryParams({ page, limit })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {NotFound}
     * @returns
     */
    async getInstallationCompany(id: number): Promise<InstallationCompany> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/installation-company/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {InvalidValue}
     * @returns
     */
    async createInstallationCompany({ name, phone }: Omit<InstallationCompany, 'id'>): Promise<InstallationCompany> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/installation-company`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, phone }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {NotFound}
     * @returns
     */
    async updateInstallationCompany({ id, name, phone }: PartialBy<InstallationCompany, 'id'>): Promise<InstallationCompany> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/installation-company/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, phone }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {NotFound}
     * @returns
     */
    async deleteInstallationCompany(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/installation-company/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getInstallationCompaniesSimplified(companyId?: number): Promise<SimplifiedInstallationCompany[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/installation-companies-simplified${this.encodeQueryParams({ companyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSupportTickets({ page, limit }: GenericPaginatedParameters): Promise<PaginatedResource<SupportTicket>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/support-tickets${this.encodeQueryParams({ page, limit })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async generateSupportTicketsReport() {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/support-tickets/report`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                translation: {
                    subject: translate('subject'),
                    createdAt: translate('createdAt'),
                    status: translate('status'),
                    updatedAt: translate('updatedAt'),
                    priority: translate('priority'),
                    agentName: translate('agentName'),
                    low: translate('lowPriority'),
                    normal: translate('normalPriority'),
                    high: translate('highPriority'),
                    urgent: translate('urgentPriority'),
                    new: translate('new'),
                    open: translate('open'),
                    hold: translate('hold'),
                    solved: translate('solved'),
                    closed: translate('closed'),
                }
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.blob();
    }

    async getSupportTicket(ticketId: number): Promise<SupportTicket> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/support-ticket/${ticketId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSupportTicketComments(ticketId: number): Promise<PaginatedResource<SupportTicketComment>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/support-ticket/${ticketId}/comments`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getTicketUser(ticketId: number, authorId: number): Promise<SupportTicketUser> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/support-ticket/${ticketId}/author/${authorId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();

    }

    async getInternetCompanies({ page, limit }: GenericPaginatedParameters): Promise<PaginatedResource<InternetCompanyList>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/internet-companies${this.encodeQueryParams({ page, limit })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {NotFound}
     * @returns
     */
    async getInternetCompany(id: number): Promise<InternetCompany> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/internet-company/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {InvalidValue}
     * @returns
     */
    async createInternetCompany({ name, phone, responsible }: Omit<InternetCompany, 'id'>): Promise<InternetCompany> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/internet-company`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, phone, responsible }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {NotFound}
     * @returns
     */
    async updateInternetCompany({ id, name, phone, responsible }: PartialBy<InternetCompany, 'id'>): Promise<InternetCompany> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/internet-company/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, phone, responsible }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @throws {NotFound}
     * @returns
     */
    async deleteInternetCompany(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/internet-company/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getInternetCompaniesSimplified(companyId?: number): Promise<SimplifiedInternetCompany[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/internet-companies-simplified${this.encodeQueryParams({ companyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUserTags(): Promise<Tag[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user/tags`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getUserTagsPaginated(params: GenericPaginatedParameters): Promise<PaginatedResource<UserTag>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user/tags/paginated${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createUserTag(params: CreateTag): Promise<Tag> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user/tag`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateUserTag(id: number, params: UpdateTag): Promise<Tag> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user/tag/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteUserTag(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user/tag/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getAllowedIps(params: GenericPaginatedParameters): Promise<PaginatedResource<AllowedIp>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/allowed-ips${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createAllowedIp(params: Omit<AllowedIp, 'id'>): Promise<AllowedIp> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/allowed-ip`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            } else if (resJson.code == 'InvalidIpAddress') {
                throw new InvalidIpAddress(resJson.code, resJson.ipAddress);
            } else if (resJson.code == 'IpAddressAlreadyExists') {
                throw new IpAddressAlreadyExists(resJson.code, resJson.ipAddress);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getAllowedIp(id: number): Promise<AllowedIp> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/allowed-ip/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async updateAllowedIp(params: PartialBy<AllowedIp, 'id'>): Promise<AllowedIp> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/allowed-ip/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'InvalidValue') {
                throw new InvalidValue(resJson.field);
            } else if (resJson.code == 'InvalidIpAddress') {
                throw new InvalidIpAddress(resJson.code, resJson.ipAddress);
            } else if (resJson.code == 'IpAddressAlreadyExists') {
                throw new IpAddressAlreadyExists(resJson.code, resJson.ipAddress);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async deleteAllowedIp(id: number): Promise<void> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/allowed-ip/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getWorkSchedules(params: GenericPaginatedParameters): Promise<PaginatedResource<WorkSchedule>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/work-schedules${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createWorkSchedule(params: Omit<WorkSchedule, 'id' | 'actingBody'>): Promise<WorkSchedule> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/work-schedule`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getWorkScheduleById(id: number): Promise<WorkSchedule> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/work-schedule/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateWorkSchedule(params: PartialBy<Omit<WorkSchedule, 'actingBodyId'>, 'id'>): Promise<WorkSchedule> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/work-schedule/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteWorkSchedule(id: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/work-schedule/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getWorkSchedulesSimplified(actingBodyId: number): Promise<WorkSchedule[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/work-schedules-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getHolidays(params: HolidaysPaginatedParameters): Promise<PaginatedResource<Holiday>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/holidays${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createHoliday(params: Omit<Holiday, 'id'>): Promise<Holiday> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/holiday`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getHolidayById(id: number): Promise<Holiday> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/holiday/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateHoliday(params: PartialBy<Holiday, 'id'>): Promise<Holiday> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/holiday/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'AlreadyExists') {
                throw new AlreadyExists();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteHoliday(id: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/holiday/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 403) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getSkill(id: number): Promise<Skill> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/skill/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSkills(params: GetResourcePaginatedByActingBodyParameters): Promise<PaginatedResource<Skill>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/skills${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSkillUsers(actingBodyId?: number): Promise<SimplifiedUser[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/skill/users${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedSkills(actingBodyId: number): Promise<DropdownResource[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/skills-simplified${this.encodeQueryParams({ actingBodyId })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createSkill({ name, description, usersToAdd, actingBodyId }: CreateSkillParameters): Promise<Skill> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/skill`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, description, usersToAdd, actingBodyId }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateSkill(id: number, { name, description, usersToAdd, usersToRm }: UpdateSkillParameters): Promise<Skill> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/skill/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ name, description, usersToAdd, usersToRm }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status == 400) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteSkill(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/access/skill/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'HasOpenVacancies') {
                throw new HasOpenVacancies();
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }
}

export const accessService = new AccessService();
